import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { ColumnFilter, ColumnSort, RiskScoreBadge } from "security-app/components/common";
import { useFetchAssetsFilterOptions } from "security-app/hooks/useAssetsAPI";
import { usePagination, useSetPagination } from "common/contexts";
import { useColumnTogglerContext } from "security-app/components/common/ColumnToggler";
import VulnerabilitiesCount from "./VulnerabilitiesCount";
const columnsWithFilter = ["hostnames", "ip_addresses", "mac_addresses"];
const getInitColumns = (showDetails) => ({
  hostnames: { visible: showDetails, group: "Machine" },
  ip_addresses: { visible: showDetails, group: "Machine" },
  mac_addresses: { visible: showDetails, group: "Machine" },
  machine_risk_score: { visible: showDetails, group: "Machine" },
  vulnerabilities: { visible: showDetails, group: "Machine" }
});
export function MachineHeaders({ showDetails }) {
  var _a, _b, _c, _d, _e;
  const pagination = usePagination();
  const setPagination = useSetPagination();
  const [filterOptions, setFilterOptions] = useState({ id: null });
  const { cacheId, columns, columnsDispatch } = useColumnTogglerContext();
  useEffect(() => {
    if (showDetails) {
      columnsDispatch({ type: "init", payload: { cacheId, initState: getInitColumns(showDetails) } });
    } else {
      columnsDispatch({ type: "reset", payload: { cacheId, colsToRemove: Object.keys(getInitColumns(showDetails)) } });
    }
  }, [cacheId, columnsDispatch, showDetails]);
  const applySort = (field, direction) => {
    setPagination({ ...pagination, orderBy: field, direction });
  };
  const { fetchAssetsFilterOptions, fetchingAssetsFilterOptions } = useFetchAssetsFilterOptions();
  useMemo(async () => {
    const machineCols = Object.keys(columns).filter((col) => columns[col].group === "Machine").filter((value) => columnsWithFilter.includes(value));
    const filters = await fetchAssetsFilterOptions({ fields: machineCols });
    setFilterOptions(filters);
  }, [columns, fetchAssetsFilterOptions]);
  const hostnamesOptions = useMemo(() => {
    if (!(filterOptions == null ? void 0 : filterOptions.hostnames)) return [];
    const options = [];
    filterOptions.hostnames.forEach((hostname) => {
      options.push({ label: hostname.toString(), value: hostname.toString(), type: "text" });
    });
    return options;
  }, [filterOptions]);
  const ipAddressesOptions = useMemo(() => {
    if (!(filterOptions == null ? void 0 : filterOptions.ip_addresses)) return [];
    const options = [];
    filterOptions.ip_addresses.forEach((ip) => {
      options.push({ label: ip.toString(), value: ip.toString(), type: "text" });
    });
    return options;
  }, [filterOptions]);
  const macAddressesOptions = useMemo(() => {
    if (!(filterOptions == null ? void 0 : filterOptions.mac_addresses)) return [];
    const options = [];
    filterOptions.mac_addresses.forEach((mac) => {
      options.push({ label: mac.toString(), value: mac.toString(), type: "text" });
    });
    return options;
  }, [filterOptions]);
  const applyFilters = (filterKey, filterValues) => {
    const auxFilters = { ...pagination.filters, [filterKey]: filterValues };
    if (!filterValues || filterValues.length === 0) delete auxFilters[filterKey];
    setPagination({ ...pagination, filters: auxFilters });
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, !fetchingAssetsFilterOptions && /* @__PURE__ */ React.createElement(React.Fragment, null, ((_a = columns.hostnames) == null ? void 0 : _a.visible) && /* @__PURE__ */ React.createElement("th", null, /* @__PURE__ */ React.createElement(
    ColumnFilter,
    {
      filterBy: "hostnames",
      type: "text",
      filterData: hostnamesOptions,
      appliedFilters: pagination.filters,
      onClose: applyFilters
    },
    "Hostnames"
  )), ((_b = columns.ip_addresses) == null ? void 0 : _b.visible) && /* @__PURE__ */ React.createElement("th", null, /* @__PURE__ */ React.createElement(
    ColumnFilter,
    {
      filterBy: "ip_addresses",
      type: "text",
      filterData: ipAddressesOptions,
      appliedFilters: pagination.filters,
      onClose: applyFilters
    },
    "IP Addresses"
  )), ((_c = columns.machine_risk_score) == null ? void 0 : _c.visible) && /* @__PURE__ */ React.createElement("th", null, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "machine_risk_score",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    "Risk Score"
  )), ((_d = columns.vulnerabilities) == null ? void 0 : _d.visible) && /* @__PURE__ */ React.createElement("th", null, /* @__PURE__ */ React.createElement(
    ColumnFilter,
    {
      filterBy: "vulnerabilities",
      type: "text",
      filterData: [],
      appliedFilters: pagination.filters,
      onClose: applyFilters
    },
    "Vulnerabilities"
  )), ((_e = columns.mac_addresses) == null ? void 0 : _e.visible) && /* @__PURE__ */ React.createElement("th", null, /* @__PURE__ */ React.createElement(
    ColumnFilter,
    {
      filterBy: "mac_addresses",
      type: "text",
      filterData: macAddressesOptions,
      appliedFilters: pagination.filters,
      onClose: applyFilters
    },
    "MAC Addresses"
  ))));
}
export function MachineData({ assetDetails, showDetails }) {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const { columns } = useColumnTogglerContext();
  return showDetails && /* @__PURE__ */ React.createElement(React.Fragment, null, ((_a = columns.hostnames) == null ? void 0 : _a.visible) && /* @__PURE__ */ React.createElement("td", null, ((_b = assetDetails.hostnames) == null ? void 0 : _b.join(", ")) || ""), ((_c = columns.ip_addresses) == null ? void 0 : _c.visible) && /* @__PURE__ */ React.createElement("td", null, ((_d = assetDetails.ip_addresses) == null ? void 0 : _d.join(", ")) || ""), ((_e = columns.machine_risk_score) == null ? void 0 : _e.visible) && /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(RiskScoreBadge, { riskScore: assetDetails.risk_score_normalized })), ((_f = columns.vulnerabilities) == null ? void 0 : _f.visible) && /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(VulnerabilitiesCount, { vulnerabilities: assetDetails.vulnerabilities })), ((_g = columns.mac_addresses) == null ? void 0 : _g.visible) && /* @__PURE__ */ React.createElement("td", null, ((_h = assetDetails.mac_addresses) == null ? void 0 : _h.join(", ")) || ""));
}
